import { useState, useEffect, type CSSProperties } from "react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import Button from "components/Button";
import Modal from "components/Modal";
import { formatCurrency } from "helpers/currency";
import VariantSwatchSelector from "./VariantSwatchSelector";
import EngravingOptionsForm from "./EngravingOptionsForm";
import ProductDescription from "./ProductDescription";
import { ENGRAVING_FONTS, ENGRAVING_SYMBOLS } from "./engravingConstants";
import { canEngraveUrn } from "./engravingEligibility";
import type { EngravingFontSize } from "./types";

/** Font size as a fraction of the image container width */
const FONT_SIZE_SCALE: Record<EngravingFontSize, number> = {
  XS: 0.028,
  S: 0.034,
  M: 0.04,
  L: 0.048,
  XL: 0.056,
};
import type { UrnProduct, EngravingOptions } from "./types";

type UrnDetailModalProps = {
  urn: UrnProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (
    urnId: string,
    variantId: string,
    quantity: number,
    engraving?: EngravingOptions | null,
  ) => void;
  currentVariantId?: string | null;
  currentQuantity?: number;
  currentEngraving?: EngravingOptions | null;
  readOnly?: boolean;
  engravingIncludedInPlan?: boolean;
  hideQuantity?: boolean;
};

export default function UrnDetailModal({
  urn,
  isOpen,
  onClose,
  onSelect,
  currentVariantId,
  currentQuantity,
  currentEngraving,
  readOnly = false,
  engravingIncludedInPlan = false,
  hideQuantity = false,
}: UrnDetailModalProps) {
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [engraving, setEngraving] = useState<EngravingOptions | null>(null);

  useEffect(() => {
    if (urn && urn.variants.length > 0) {
      setSelectedVariantId(currentVariantId || urn.variants[0].id);
      setQuantity(currentQuantity ?? 1);
      setEngraving(canEngraveUrn(urn) ? (currentEngraving ?? null) : null);
    }
  }, [urn]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!urn) return null;

  const selectedVariant = urn.variants.find((v) => v.id === selectedVariantId);
  const isEngravable = canEngraveUrn(urn);

  return (
    <Modal isOpen={isOpen} onClose={onClose} fullScreen>
      {/* Two-column on md+, stacked on mobile */}
      <div className="flex h-full flex-col md:flex-row md:gap-10">
        {/* Left: Image with engraving preview */}
        {/* Mobile pt-8 clears Modal's absolutely-positioned close button */}
        <div className="shrink-0 pt-8 md:sticky md:top-0 md:w-1/2 md:pt-0">
          {/* Square container — text sizes use cqw (% of container width) */}
          <div
            className="relative mx-auto aspect-square max-h-[40vh] md:max-h-[70vh]"
            style={{ containerType: "inline-size" } as CSSProperties}
          >
            {selectedVariant?.image || urn.image ? (
              <img
                src={selectedVariant?.image || urn.image}
                alt={urn.name}
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-xl bg-gray-200">
                <span className="text-gray-400">No image</span>
              </div>
            )}
            {/* Engraving preview overlay */}
            {engraving && (
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-[20%]">
                {engraving.symbolId &&
                  (() => {
                    const symbol = ENGRAVING_SYMBOLS.find(
                      (s) => s.id === engraving.symbolId,
                    );
                    return symbol ? (
                      <img
                        src={symbol.image}
                        alt={symbol.name}
                        className="drop-shadow-md"
                        style={{
                          width: "8cqw",
                          height: "8cqw",
                          marginBottom: "0.5cqw",
                          objectFit: "contain",
                        }}
                      />
                    ) : null;
                  })()}
                {engraving.lines.map((line, i) => {
                  const fontConfig = ENGRAVING_FONTS.find(
                    (f) => f.value === engraving.font,
                  );
                  return (
                    <span
                      key={i}
                      className="text-white drop-shadow-md"
                      style={{
                        fontFamily: fontConfig?.fontFamily,
                        fontStyle: fontConfig?.italic ? "italic" : "normal",
                        fontSize: `${FONT_SIZE_SCALE[line.fontSize] * 100}cqw`,
                        lineHeight: 1.4,
                      }}
                    >
                      {line.text || (i === 0 ? "Name" : `Line ${i + 1}`)}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Details */}
        <div className="flex flex-col md:w-1/2 md:overflow-y-auto md:py-2">
          {/* Name + Price */}
          <div>
            <h2 className="font-serif text-3xl italic text-evergreen md:text-4xl">
              {urn.name}
            </h2>
            <p className="mt-1 text-2xl font-bold text-evergreen">
              {formatCurrency(selectedVariant?.price ?? urn.price)}
            </p>
          </div>

          {/* Description */}
          <ProductDescription
            description={urn.description}
            descriptionHtml={urn.descriptionHtml}
          />

          {/* Specs */}
          {(urn.material || urn.dimensions || urn.capacity) && (
            <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 border-b border-t border-oat py-4">
              {urn.material && (
                <div>
                  <dt className="font-semibold text-evergreen">Material</dt>
                  <dd className="mt-1 text-soil">{urn.material}</dd>
                </div>
              )}
              {urn.capacity && (
                <div>
                  <dt className="font-semibold text-evergreen">Capacity</dt>
                  <dd className="mt-1 text-soil">{urn.capacity}</dd>
                </div>
              )}
              {urn.dimensions && (
                <div>
                  <dt className="font-semibold text-evergreen">Dimensions</dt>
                  <dd className="mt-1 text-soil">
                    {urn.dimensions}
                    <span className="mt-1 block text-sm text-soil/70">
                      All urns accommodate standard adult cremated remains.
                    </span>
                  </dd>
                </div>
              )}
            </dl>
          )}

          {/* Color + Quantity row */}
          <div
            className={`mt-6 flex items-end ${hideQuantity ? "" : "justify-between"} gap-4`}
          >
            {/* Color swatches */}
            <VariantSwatchSelector
              variants={urn.variants}
              selectedVariantId={selectedVariantId}
              onSelect={setSelectedVariantId}
              readOnly={readOnly}
            />

            {/* Quantity */}
            {!hideQuantity && (
              <div className="shrink-0">
                <p className="font-semibold text-evergreen">Quantity</p>
                {readOnly ? (
                  <p className="mt-2 text-center text-xl font-bold text-evergreen">
                    {quantity}
                  </p>
                ) : (
                  <div className="mt-2 flex items-center gap-4">
                    <button
                      onClick={() => setQuantity((q) => Math.max(0, q - 1))}
                      disabled={quantity <= 0}
                      className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-oat text-evergreen transition hover:bg-oat disabled:opacity-40 disabled:hover:bg-transparent"
                      aria-label="Decrease quantity"
                    >
                      <MinusIcon className="h-5 w-5" />
                    </button>
                    <span className="min-w-6 text-center text-xl font-bold text-evergreen">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-oat text-evergreen transition hover:bg-oat"
                      aria-label="Increase quantity"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Engraving options */}
          {isEngravable && (
            <div className="mt-6">
              <EngravingOptionsForm
                engraving={engraving}
                onChange={setEngraving}
                readOnly={readOnly}
                includedInPlan={engravingIncludedInPlan}
              />
            </div>
          )}

          {/* CTA pinned to bottom */}
          {!readOnly && (
            <div className="mt-auto pt-6">
              <Button
                onClick={() => {
                  onSelect(
                    urn.id,
                    selectedVariantId,
                    quantity,
                    isEngravable ? engraving : null,
                  );
                  onClose();
                }}
                variant="primary"
                fullWidth
              >
                Choose this urn
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
